import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from './Navbar.js';
import labService from './labService.js';
import authService from '../auth/authService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX } from '@fortawesome/free-solid-svg-icons';
import HashLoader from "react-spinners/HashLoader";
import './css/Lab.css';
import './css/DefineEndpoits.css';
import Visualization from './Visualization.js';
import ModelsByCategory from './ModelsByCategory.js';

const DefineEndpoints = ({ isCollapsed, toggleSidebar }) => {
  const { analysisPk } = useParams();
  const navigate = useNavigate();

  // Stany dla danych z bazy i użytkownika
  const [endpointDetails, setEndpointDetails] = useState(null);
  const [modelsList, setModelsList] = useState([]);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Stany dla formularza
  const [selectedModels, setSelectedModels] = useState({});
  const [expandedModels, setExpandedModels] = useState([]);
  const [analysisName, setAnalysisName] = useState('');

  useEffect(() => {
    const token = authService.getCurrentUser()?.access;

    if (!token) {
      setError("You must be logged in to view this page.");
      setLoading(false);
      return;
    }

    // Odtwarzamy dane z kroku 1
    const savedEndpointId = localStorage.getItem('selectedEndpoint');
    const savedAnalysisName = localStorage.getItem('analysisName');

    if (!savedEndpointId) {
      // Jeśli nie ma zapisanego wyboru (np. użytkownik wpisał adres z palca), wyrzucamy go do początku
      navigate('/choose-substance');
      return;
    }

    const endpointId = JSON.parse(savedEndpointId);
    if (savedAnalysisName) {
      setAnalysisName(JSON.parse(savedAnalysisName));
    }

    // Odtwarzamy zapisane modele TYLKO jeśli użytkownik cofnął się z kroku 3 do kroku 2
    const savedModels = localStorage.getItem('selectedModels');
    if (savedModels) {
      setSelectedModels(JSON.parse(savedModels));
    }

    const fetchEndpointData = async () => {
      try {
        // Równoległe pobieranie szczegółów endpointu, modeli dla niego oraz danych o tokenach użytkownika
        const [endpointRes, modelsRes, userRes] = await Promise.all([
          labService.getEndpointDetail(endpointId, token),
          labService.getMLModelsByEndpoint(endpointId, token),
          labService.getUserInfo(token)
        ]);

        setEndpointDetails(endpointRes.data);
        setModelsList(modelsRes.data);
        setUser(userRes.data);

        // Automatycznie rozwijamy wszystkie dostępne kategorie
        const modelIds = modelsRes.data.map(m => m.id);
        setExpandedModels(modelIds);

      } catch (error) {
        console.error('Error fetching data for define endpoints:', error);
        setError('Failed to load models. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchEndpointData();
  }, [navigate]);

  const handleNext = () => {
    // Zabezpieczenia (użytkownik nie może pójść dalej, jeśli nic nie zaznaczył)
    if (!selectedModels || Object.keys(selectedModels).length === 0) return;
    if (user && user.coins < totalCost) return;

    // Aktualizujemy localStorage o nowe zaznaczenia (idziemy do przodu, więc chcemy to pamiętać)
    localStorage.setItem('selectedModels', JSON.stringify(selectedModels));

    // Zawsze przechodzimy do "Define Nanoform" - krok trzeci
    navigate('/define-nanoform');
  };

  const handleBack = () => {
    // Użytkownik wraca do kroku 1 - CZYŚCIMY DANE WYBORU MODELI I DESKRYPTORÓW
    localStorage.removeItem('selectedModels');
    localStorage.removeItem('descriptorValues');

    navigate('/choose-substance');
  };

  const closeSelectModels = () => {
    // Użytkownik wychodzi całkowicie z kreatora - CZYŚCIMY WSZYSTKO
    localStorage.removeItem('selectedEndpoint');
    localStorage.removeItem('analysisName');
    localStorage.removeItem('selectedModels');
    localStorage.removeItem('descriptorValues');

    navigate('/laboratory');
  };

  const handleCheckboxChange = (modelId, cellLine) => {
    setSelectedModels((prev) => {
      const current = prev[modelId] || [];

      if (current.includes(cellLine)) {
        const updated = current.filter((cl) => cl !== cellLine);
        const newState = { ...prev, [modelId]: updated };
        if (updated.length === 0) delete newState[modelId];
        return newState;
      } else {
        return { ...prev, [modelId]: [...current, cellLine] };
      }
    });
  };

  const handleAllCheckboxChange = (modelId, allCellLines) => {
    setSelectedModels((prev) => {
      const currentlySelected = prev[modelId] || [];
      const allSelected = allCellLines.every((cl) => currentlySelected.includes(cl));

      if (allSelected) {
        const newState = { ...prev };
        delete newState[modelId];
        return newState;
      } else {
        return { ...prev, [modelId]: allCellLines };
      }
    });
  };

  const toggleExpand = (modelId) => {
    setExpandedModels((prev) =>
      prev.includes(modelId) ? prev.filter((id) => id !== modelId) : [...prev, modelId]
    );
  };

  // Obliczanie całkowitego kosztu za wybrane modele
  const totalCost = Object.entries(selectedModels).reduce((acc, [modelId, cellLines]) => {
    const model = modelsList.find(m => m.id === parseInt(modelId));
    return acc + (model ? model.model_price * cellLines.length : 0);
  }, 0);

  // Helpers dla kolorów
  const getCoreColor = (name) => {
    if (!name) return '#b6b6b6';
    const lowerName = name.toLowerCase();
    if (lowerName.includes('sio2')) return '#e2d587';
    if (lowerName.includes('tio2')) return '#949494';
    if (lowerName.includes('zno')) return '#f55353';
    if (lowerName.includes('ag')) return '#c0c0c0';
    return '#b6b6b6';
  };

  if (loading) return <div style={{ marginTop: '100px', textAlign: 'center' }}><HashLoader color="#1B4480" size={50} /></div>;
  if (error) return <div style={{ marginTop: '100px', textAlign: 'center', color: 'red' }}>{error}</div>;

  // Pobieramy nazwę substancji do wyświetlenia z pobranych szczegółów (lub dajemy 'Nanoform' jako fallback)
  const coreName = endpointDetails?.endpoint_name || 'Nanoform';
  const coreColor = getCoreColor(coreName);

  // Określamy, czy można kliknąć Next (musi być min. 1 model + wystarczająca liczba tokenów)
  const isNextReady = Object.keys(selectedModels).length > 0 && (!user || user.coins >= totalCost);

  return (
    <div className='lab-container'>
      <div className='new-pred-container'>
        <div className='np-tabs'>
          <button onClick={closeSelectModels} className='x-but'>
            <FontAwesomeIcon className='icon-x' icon={faX} />
          </button>
          <div className='np-steps'>
            <p className='p-step'>STEP 1</p>
            <p className='mar-btm-few previous-step'>Select substance</p>
            <div className="step-progress"></div>
          </div>
          <div className='np-steps active-tab-cm'>
            <p className='p-step'>STEP 2</p>
            <p className='mar-btm-few'>Select endpoints</p>
            <div className="step-progress"></div>
          </div>
          <div className='np-steps'>
            <p className='p-step'>STEP 3</p>
            <p className='mar-btm-few'>Define nanoform</p>
          </div>
        </div>

        {endpointDetails && modelsList ? (
          <div className='new-study-wizard-body'>
            <div className='new-study-wizard-configurator'>
              <div className="define-endpoint">
                <div className="divider"></div>
                <div className="core-container">
                  <h2 className="core-header">Endpoints</h2>
                </div>
                <div className='models-by-category-wrapper'>
                  {modelsList.length === 0 ? (
                    <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>No models currently available for this substance.</p>
                  ) : (
                    <ModelsByCategory
                      modelsList={modelsList}
                      expandedModels={expandedModels}
                      selectedModels={selectedModels}
                      user={user}
                      toggleExpand={toggleExpand}
                      handleAllCheckboxChange={handleAllCheckboxChange}
                      handleCheckboxChange={handleCheckboxChange}
                    />
                  )}
                </div>

                {/* Ostrzeżenie o braku tokenów umieszczone nad guzikami, tak jak było to dawniej */}
                {user && user.coins < totalCost && (
                  <div className="message-left" style={{ marginTop: '15px' }}>
                    <p className="not-enough-tokens" style={{ color: '#ff4d4f', fontSize: '14px', textAlign: 'right', marginBottom: '10px' }}>
                      <span className="boldy">You do not have enough tokens </span>to make this number of predictions. Go to the Wallet tab to top up your account.
                    </p>
                  </div>
                )}

                <div className="next-btn-wrapper">
                  <div className="next-btn-wrapper-inside">
                    <button onClick={handleBack} type="button" className="back-button">Back</button>
                    <button
                      onClick={handleNext}
                      className={isNextReady ? 'next-button' : 'disabled-button'}
                      disabled={!isNextReady}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='new-study-wizard-visualization'>
              <Visualization
                core={{ name: coreName }}
                coreModification={null}
                coreColor={coreColor}
                surfaceFunctionalization={false}
                surfaceFunctionalizationColor="#9ed2ff"
                lineColor="#7fb13a"
                isNanoformImageGenerated={false}
                visualizationType={'inconclusive'}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DefineEndpoints;
